import { createFileRoute, Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import {
  ArrowRight,
  Plane,
  Clock,
  Shield,
  MessageCircle,
  Star,
  Bot,
  LayoutDashboard,
  Car,
  TrendingUp,
  Radio,
  Globe,
  Building2,
  Users,
  MapPin,
  Calendar,
  Luggage,
  ChevronDown,
  BadgeCheck,
  Headphones,
  Lock,
  RefreshCw,
} from "lucide-react";
import heroImg from "@/assets/hero.webp";
import vEconomy from "@/assets/v-economy.webp";
import vFamily from "@/assets/v-family.webp";
import vPremiumSedan from "@/assets/v-premium-sedan.webp";
import vLarge from "@/assets/v-large.webp";
import vPremiumVan from "@/assets/v-premium-van.webp";
import vLuxury from "@/assets/v-luxury.webp";
import partnerToMoroccoWeGo from "@/assets/partner-tomoroccowego.webp";
import partnerBookaway from "@/assets/partner-bookaway.png";
import partnerWilaya from "@/assets/partner-wilaya-souss-massa.png.asset.json";
import partnerCRI from "@/assets/partner-cri-oriental.png.asset.json";
import partnerRegionSoussMassa from "@/assets/partner-region-souss-massa.jpg.asset.json";
import partnerAbrid from "@/assets/partner-abrid.png.asset.json";
import partnerSouthbridge from "@/assets/partner-southbridge.jpg.asset.json";
import partnerCGEM from "@/assets/partner-cgem-souss-massa.png.asset.json";
import cityCasablancaAsset from "@/assets/city-casablanca.jpg.asset.json";
import cityMarrakechAsset from "@/assets/city-marrakech.jpg.asset.json";
import cityFez from "@/assets/city-fez.webp";
import cityAgadirAsset from "@/assets/city-agadir.jpg.asset.json";
import cityTangierAsset from "@/assets/city-tangier.jpg.asset.json";
import cityRabatAsset from "@/assets/city-rabat.jpg.asset.json";
const cityCasablanca = cityCasablancaAsset.url;
const cityMarrakech = cityMarrakechAsset.url;
const cityAgadir = cityAgadirAsset.url;
const cityTangier = cityTangierAsset.url;
const cityRabat = cityRabatAsset.url;

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "Private Airport Transfers in Morocco | Bacha Way Travel" },
      { name: "description", content: "Private airport transfers in Morocco — VIP VTC, Agadir, Marrakech, Taghazout. Fixed prices, English-speaking drivers, 24/7 flight tracking." },
      { property: "og:title", content: "Private Airport Transfers in Morocco | Bacha Way Travel" },
      { property: "og:description", content: "Premium private airport transfers across Morocco. Instant quotes, vetted drivers, flight tracking, 24/7 WhatsApp." },
      { property: "og:url", content: "https://bachawaytravel.com/" },
      { name: "twitter:title", content: "Private Airport Transfers in Morocco | Bacha Way Travel" },
      { name: "twitter:description", content: "Premium private airport transfers across Morocco — instant quotes, vetted drivers, 24/7 support." },
    ],
    links: [
      { rel: "canonical", href: "https://bachawaytravel.com/" },
      { rel: "preload", as: "image", href: heroImg, fetchpriority: "high" } as unknown as { rel: string; href: string },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            { "@type": "Question", name: "How far in advance should I book?", acceptedAnswer: { "@type": "Answer", text: "We recommend booking at least 24 hours in advance to guarantee availability, especially during peak season. Last-minute bookings can often be accommodated via WhatsApp." } },
            { "@type": "Question", name: "Is the price really fixed?", acceptedAnswer: { "@type": "Answer", text: "Yes. The price you see at checkout is the total price. No surge pricing, no hidden fees, no extra charges for luggage, child seats or flight delays." } },
            { "@type": "Question", name: "What happens if my flight is delayed?", acceptedAnswer: { "@type": "Answer", text: "We track your flight in real time. Your chauffeur will adjust pickup automatically at no extra cost, with up to 60 minutes of free wait time after landing." } },
            { "@type": "Question", name: "Can I pay in cash or by card?", acceptedAnswer: { "@type": "Answer", text: "Both. Pay securely online with card, or settle in cash with your chauffeur in EUR, USD or MAD." } },
            { "@type": "Question", name: "Do you provide child seats?", acceptedAnswer: { "@type": "Answer", text: "Yes — infant, child and booster seats are available free of charge. Just let us know during booking." } },
            { "@type": "Question", name: "Which cities do you serve?", acceptedAnswer: { "@type": "Answer", text: "Casablanca, Marrakech, Fez, Agadir, Tangier and Rabat, plus intercity transfers and custom day-trip itineraries across Morocco." } },
          ],
        }),
      },
    ],
  }),
  component: Index,
});

const stats = [
  { value: "200+", label: "Daily transfers", icon: Car },
  { value: "6", label: "Cities covered", icon: MapPin },
  { value: "Since 2018", label: "On the ground", icon: Calendar },
  { value: "4.9/5", label: "Customer rating", icon: Star },
];

const techIcons = [Bot, LayoutDashboard, Car, TrendingUp, Radio, Globe];
const audienceIcons = [Building2, Car, Users];
const fleetImgs = [vEconomy, vFamily, vPremiumSedan, vLarge, vPremiumVan, vLuxury];
const fleetCounts = [
  { pax: 4, bags: 2 }, { pax: 4, bags: 3 }, { pax: 3, bags: 3 },
  { pax: 7, bags: 7 }, { pax: 4, bags: 6 }, { pax: 3, bags: 2 },
];

const airports = [
  { code: "CMN", name: "Casablanca", desc: "Mohammed V Airport", img: cityCasablanca },
  { code: "RAK", name: "Marrakech", desc: "Menara Airport", img: cityMarrakech },
  { code: "FEZ", name: "Fez", desc: "Saïss Airport", img: cityFez },
  { code: "AGA", name: "Agadir", desc: "Al Massira Airport", img: cityAgadir },
  { code: "TNG", name: "Tangier", desc: "Ibn Battouta Airport", img: cityTangier },
  { code: "RBA", name: "Rabat", desc: "Salé Airport", img: cityRabat },
];

function Index() {
  const { t } = useTranslation();
  return (
    <>
      {/* Hero */}
      <section className="relative overflow-hidden bg-hero-navy text-white">
        <div className="absolute inset-0 opacity-20">
          <img src={heroImg} alt="Luxury private chauffeur car for VIP airport transfers in Morocco" fetchPriority="high" decoding="async" width={1920} height={1080} className="h-full w-full object-cover mix-blend-overlay" />
        </div>
        <div className="relative mx-auto max-w-7xl px-6 pt-20 pb-32 md:pt-28 md:pb-44 text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-4 py-1.5 text-[10px] md:text-xs uppercase tracking-[0.25em] text-white/80 backdrop-blur">
            {t("hero.badge")}
          </div>
          <h1 className="mt-8 mx-auto max-w-4xl text-4xl font-bold leading-[1.05] md:text-6xl lg:text-7xl">
            {t("hero.title1")}
            <br />
            <span className="text-gradient-sky">{t("hero.title2")}</span>
          </h1>
          <p className="mt-6 mx-auto max-w-2xl text-base md:text-lg text-white/70">
            {t("hero.subtitle")}
          </p>
          <h2 className="sr-only">VIP transfer Morocco · private driver Agadir · Marrakech airport transfer · luxury VTC Taghazout</h2>
          <div className="mt-6 flex justify-center">
            <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-1.5 text-xs font-medium text-white/90 backdrop-blur">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
              {t("hero.tag")}
            </span>
          </div>
          <div className="mt-5 flex flex-wrap justify-center gap-3">
            <Link
              to="/booking"
              className="group inline-flex items-center gap-2 rounded-full bg-white px-7 py-3.5 text-sm font-semibold text-primary transition-transform hover:scale-[1.02]"
            >
              {t("hero.book")}
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </Link>
            <Link
              to="/fleet"
              className="inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/5 px-7 py-3.5 text-sm font-semibold text-white backdrop-blur hover:bg-white/10"
            >
              {t("hero.fleet")}
            </Link>
          </div>
        </div>


      </section>

      {/* Trust strip */}
      <section className="py-8 md:py-12 bg-background">
        <div className="mx-auto grid max-w-7xl grid-cols-2 gap-x-3 gap-y-4 px-4 md:grid-cols-4 md:gap-y-8 md:px-6">
          {[
            { icon: Shield, label: t("trust.fixedPrice"), sub: t("trust.fixedPriceSub") },
            { icon: Plane, label: t("trust.flight"), sub: t("trust.flightSub") },
            { icon: Clock, label: t("trust.meet"), sub: t("trust.meetSub") },
            { icon: MessageCircle, label: t("trust.support"), sub: t("trust.supportSub") },
          ].map((f) => (
            <div key={f.label} className="flex items-start gap-2 md:items-center md:gap-4 md:px-2">
              <f.icon className="h-4 w-4 shrink-0 text-primary md:h-6 md:w-6" />
              <div className="min-w-0">
                <div className="text-xs font-semibold text-foreground md:text-sm">{f.label}</div>
                <div className="text-[11px] leading-snug text-muted-foreground md:text-xs">{f.sub}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Technology */}
      <section className="py-24 bg-secondary/40">
        <div className="mx-auto max-w-7xl px-6">
          <div className="text-xs uppercase tracking-[0.25em] text-primary font-semibold">{t("tech.eyebrow")}</div>
          <h2 className="mt-3 max-w-3xl text-3xl md:text-5xl font-bold leading-tight">
            {t("tech.title1")} <span className="text-primary">{t("tech.title2")}</span>
          </h2>
          <p className="mt-5 max-w-2xl text-muted-foreground">{t("tech.lead")}</p>

          <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {techIcons.map((Icon, i) => (
              <div key={i} className="group rounded-2xl border border-border bg-card p-7 transition-all hover:border-primary/40 hover:shadow-xl hover:shadow-primary/5 hover:-translate-y-1">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                  <Icon className="h-6 w-6" />
                </div>
                <h3 className="mt-5 text-lg font-semibold">{t(`tech.f${i + 1}t`)}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{t(`tech.f${i + 1}d`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who we serve */}
      <section className="py-24 bg-background">
        <div className="mx-auto max-w-7xl px-6">
          <div className="text-xs uppercase tracking-[0.25em] text-primary font-semibold">{t("audiences.eyebrow")}</div>
          <h2 className="mt-3 max-w-3xl text-3xl md:text-5xl font-bold">{t("audiences.title")}</h2>
          <p className="mt-4 max-w-xl text-muted-foreground">{t("audiences.lead")}</p>

          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {audienceIcons.map((Icon, i) => (
              <div key={i} className="relative overflow-hidden rounded-3xl border border-border bg-gradient-to-br from-card to-secondary/30 p-8 hover:border-primary/40 transition-colors">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary text-primary-foreground">
                  <Icon className="h-6 w-6" />
                </div>
                <div className="mt-6 text-xs uppercase tracking-[0.2em] text-primary font-semibold">{t(`audiences.a${i + 1}l`)}</div>
                <h3 className="mt-2 text-2xl font-bold">{t(`audiences.a${i + 1}t`)}</h3>
                <p className="mt-3 text-sm text-muted-foreground">{t(`audiences.a${i + 1}d`)}</p>
                <Link to="/contact" className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:gap-2.5 transition-all">
                  {t(`audiences.a${i + 1}c`)} <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Fleet */}
      <section className="py-24 bg-secondary/40 overflow-hidden">
        <div className="mx-auto max-w-7xl px-6">
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div>
              <div className="text-xs uppercase tracking-[0.25em] text-primary font-semibold">{t("fleetSec.eyebrow")}</div>
              <h2 className="mt-3 text-3xl md:text-5xl font-bold">{t("fleetSec.title")}</h2>
            </div>
            <Link to="/fleet" className="text-sm font-semibold text-primary hover:underline">
              {t("fleetSec.seeAll")}
            </Link>
          </div>
        </div>

        <div className="mt-10 marquee-mask overflow-x-auto cursor-grab active:cursor-grabbing">
          <div className="flex w-max animate-marquee-rtl gap-5 px-6">
            {[...Array(2)].flatMap((_, dup) =>
              fleetImgs.map((img, i) => (
                <div
                  key={`${dup}-${i}`}
                  className="group w-[220px] shrink-0 overflow-hidden rounded-xl border border-border bg-card hover:shadow-lg hover:shadow-primary/10 transition-shadow"
                  aria-hidden={dup === 1 ? true : undefined}
                >
                  <div className="h-28 overflow-hidden bg-gradient-to-br from-secondary/60 to-background flex items-center justify-center relative p-4">
                    <img src={img} alt={`${t(`fleetSec.v${i + 1}n`)} class private transfer vehicle in Morocco`} loading="lazy" className="max-h-full max-w-full object-contain transition-transform duration-500 group-hover:scale-105" />
                  </div>
                  <div className="p-4">
                    <h3 className="text-sm font-bold">{t(`fleetSec.v${i + 1}n`)}</h3>
                    <div className="mt-1.5 flex items-center gap-3 text-[11px] text-muted-foreground">
                      <span className="inline-flex items-center gap-1">
                        <Users className="h-3 w-3" /> {fleetCounts[i].pax} {t("fleetSec.pax")}
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <Luggage className="h-3 w-3" /> {fleetCounts[i].bags} {t("fleetSec.bags")}
                      </span>
                    </div>
                    <p className="mt-2 text-[11px] text-muted-foreground line-clamp-2">{t(`fleetSec.v${i + 1}d`)}</p>
                    <div className="mt-3 flex flex-wrap gap-1.5 text-[10px] text-muted-foreground">
                      <span className="rounded-full bg-secondary px-2 py-0.5">{t("fleetSec.perk1")}</span>
                      <span className="rounded-full bg-secondary px-2 py-0.5">{t("fleetSec.perk2")}</span>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </section>

      {/* Airports */}
      <section className="py-24 bg-background">
        <div className="mx-auto max-w-7xl px-6">
          <div className="text-xs uppercase tracking-[0.25em] text-primary font-semibold">{t("airportsSec.eyebrow")}</div>
          <h2 className="mt-3 text-3xl md:text-5xl font-bold">{t("airportsSec.title")}</h2>
          <p className="mt-4 max-w-2xl text-muted-foreground">{t("airportsSec.lead")}</p>

          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {airports.map((a) => (
              <div key={a.code} className="group relative overflow-hidden rounded-2xl border border-border bg-card hover:border-primary/40 hover:shadow-xl hover:shadow-primary/10 transition-all">
                <div className="aspect-[16/10] overflow-hidden">
                  <img src={a.img} alt={`${a.name} — ${a.desc}`} loading="lazy" width={1280} height={896} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                </div>
                <div className="flex items-center gap-4 p-5">
                  <div className="font-bold text-3xl text-primary w-16">{a.code}</div>
                  <div>
                    <h3 className="text-base font-semibold">{a.name}</h3>
                    <div className="text-xs text-muted-foreground">{a.desc}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-secondary/40">
        <div className="mx-auto max-w-7xl px-6">
          <div className="text-xs uppercase tracking-[0.25em] text-primary font-semibold">{t("testi.eyebrow")}</div>
          <h2 className="mt-3 text-3xl md:text-5xl font-bold">{t("testi.title")}</h2>

          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="rounded-2xl border border-border bg-card p-7">
                <div className="flex gap-0.5 text-primary">
                  {Array.from({ length: 5 }).map((_, k) => (
                    <Star key={k} className="h-4 w-4 fill-current" />
                  ))}
                </div>
                <p className="mt-4 text-foreground/90 italic leading-relaxed">"{t(`testi.q${i}`)}"</p>
                <div className="mt-5 text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold">{t(`testi.w${i}`)}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Partners */}
      <section className="py-20 bg-background">
        <div className="mx-auto max-w-7xl px-6 text-center">
          <div className="text-xs uppercase tracking-[0.25em] text-primary font-semibold">{t("partnersSec.eyebrow")}</div>
          <h2 className="mt-3 text-3xl md:text-5xl font-bold">{t("partnersSec.title")}</h2>
          <p className="mt-4 max-w-xl mx-auto text-muted-foreground">{t("partnersSec.lead")}</p>
          <div className="mt-12 marquee-mask overflow-hidden">
            <div className="flex w-max animate-marquee-ltr gap-8 sm:gap-12">
              {[...Array(2)].flatMap((_, dup) =>
                [
                  { name: "To Morocco We Go", img: partnerToMoroccoWeGo, href: "#" },
                  { name: "Bookaway", img: partnerBookaway, href: "https://www.bookaway.com" },
                  { name: "Wilaya Souss-Massa", img: partnerWilaya.url, href: "#" },
                  { name: "CRI Souss massa", img: partnerCRI.url, href: "#" },
                  { name: "Région Souss-Massa", img: partnerRegionSoussMassa.url, href: "#" },
                  { name: "Abrid", img: partnerAbrid.url, href: "#" },
                  { name: "SouthBridge", img: partnerSouthbridge.url, href: "#" },
                  { name: "CGEM Souss-Massa", img: partnerCGEM.url, href: "#" },
                ].map((p) => (
                  <a key={`${dup}-${p.name}`} href={p.href} target={p.href.startsWith("http") ? "_blank" : undefined} rel={p.href.startsWith("http") ? "noopener noreferrer" : undefined} className="group flex shrink-0 flex-col items-center gap-3" aria-label={p.name} aria-hidden={dup === 1 ? true : undefined}>
                    <div className="h-20 w-20 sm:h-24 sm:w-24 md:h-28 md:w-28 overflow-hidden rounded-full border border-border bg-white p-3 flex items-center justify-center transition-transform group-hover:scale-110 shadow-sm">
                      <img src={p.img} alt={`${p.name} — Bacha Way Travel partner logo`} className="h-full w-full object-contain" loading="lazy" />
                    </div>
                    <div className="text-[10px] sm:text-[11px] uppercase tracking-[0.16em] text-muted-foreground group-hover:text-primary font-semibold text-center w-24 sm:w-28">
                      {p.name}
                    </div>
                  </a>
                ))
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Trust badges + FAQ */}
      <section className="py-24 bg-secondary/40">
        <div className="mx-auto max-w-7xl px-6">
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { icon: Lock, t: "Secure payment", d: "256-bit SSL & PCI-compliant gateways." },
              { icon: BadgeCheck, t: "Vetted chauffeurs", d: "Licensed, English-speaking, background checked." },
              { icon: RefreshCw, t: "Free cancellation", d: "Cancel up to 24h before pickup, no fees." },
              { icon: Headphones, t: "24/7 support", d: "Real humans on WhatsApp, anytime." },
            ].map((b) => (
              <div key={b.t} className="flex items-start gap-4 rounded-2xl border border-border bg-card p-5">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <b.icon className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-sm font-semibold">{b.t}</div>
                  <div className="mt-1 text-xs text-muted-foreground leading-relaxed">{b.d}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-16 grid gap-10 lg:grid-cols-[1fr_2fr]">
            <div>
              <div className="text-xs uppercase tracking-[0.25em] text-primary font-semibold">FAQ</div>
              <h2 className="mt-3 text-3xl md:text-4xl font-bold leading-tight">Answers, before you ask.</h2>
              <p className="mt-4 text-muted-foreground">Everything you need to know about booking your private transfer with Bacha Way Travel.</p>
            </div>
            <div className="divide-y divide-border rounded-2xl border border-border bg-card">
              {[
                { q: "How far in advance should I book?", a: "We recommend booking at least 24 hours in advance to guarantee availability, especially during peak season. Last-minute bookings can often be accommodated — message us on WhatsApp." },
                { q: "Is the price really fixed?", a: "Yes. The price you see at checkout is the total price. No surge pricing, no hidden fees, no extra charges for luggage, child seats or flight delays." },
                { q: "What happens if my flight is delayed?", a: "We track your flight in real time. Your chauffeur will adjust pickup automatically — at no extra cost, with up to 60 minutes of free wait time after landing." },
                { q: "Can I pay in cash or by card?", a: "Both. Pay securely online with card, or settle in cash with your chauffeur (EUR, USD or MAD). Receipts available on request." },
                { q: "Do you provide child seats?", a: "Yes — infant, child and booster seats are available free of charge. Just let us know during booking." },
                { q: "Which cities do you serve?", a: "Casablanca, Marrakech, Fez, Agadir, Tangier and Rabat — plus intercity transfers and custom day-trip itineraries across Morocco." },
              ].map((f, i) => (
                <details key={i} className="group p-5 [&_summary::-webkit-details-marker]:hidden">
                  <summary className="flex cursor-pointer items-center justify-between gap-4 text-sm md:text-base font-semibold text-foreground">
                    {f.q}
                    <ChevronDown className="h-4 w-4 shrink-0 text-primary transition-transform group-open:rotate-180" />
                  </summary>
                  <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-background">
        <div className="mx-auto max-w-7xl px-6">
          <div className="overflow-hidden rounded-3xl bg-hero-navy text-white p-10 md:p-16 text-center">
            <h2 className="text-3xl md:text-5xl font-bold">{t("homeCta.title")}</h2>
            <p className="mt-4 max-w-xl mx-auto text-white/70">{t("homeCta.lead")}</p>
            <Link to="/booking" className="mt-8 inline-flex items-center gap-2 rounded-full bg-white px-8 py-4 text-sm font-semibold text-primary transition-transform hover:scale-[1.02]">
              {t("homeCta.btn")}
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
